/*
 * What one AI Recommendation says on its row (owner 10-06: "shorter and to the
 * point - not in plain English"; CONTRACT §19, careiq.n3.3). Pure: no React.
 *
 *   title   `headline` (clinician shorthand, at most 60 characters, guarded by
 *           the server: every number and date is the item's own) when the
 *           body has one; else the sentence (`text`) clipped at a clause
 *           boundary: a sentence end, a semicolon, a spaced dash, or before a
 *           "given / due to / because ..." justification. A clip never cuts
 *           inside a number or a word, and a sentence with no boundary early
 *           enough is shown whole (it wraps; it is never line-clamped).
 *   halves  a served headline split at its arrow into what it finds and what to
 *           do (owner 10-07: no arrow on the row; rowText / splitHeadline); a
 *           fallback sentence is never split.
 *   detail  the full sentence, behind the item's (i), when the title does not
 *           already say all of it.
 *   prov    the (i)'s grey line: the merged sources, the clinical "when"
 *           ("open since Sep 11 · 2 visits") and a reopen note; a bare
 *           guideline acronym ("USPSTF") only when no guideline link carries it.
 * The row's value lead, lanes and colour level: recLanes.js.
 */
import { SOURCE_LABEL } from "./recsModel";

export const TITLE_MAX = 96;       // a fallback title longer than this is clipped (when it can be)
const TITLE_MIN = 48;              // never clip down to less than this (a risk "due to ..." is its content)
const TITLE_HARD = 140;            // the first boundary may sit up to here when none is earlier

const str = (v) => (v == null ? "" : String(v));
const squash = (s) => str(s).replace(/\s+/g, " ").trim();
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// A period that ends an abbreviation, not a sentence ("e.g. atorvastatin").
const ABBREV = /(?:^|[\s(])(?:e\.g|i\.e|vs|approx|incl|etc|Dr|Mr|Mrs|Ms|St|No|Fig|U\.S|a\.m|p\.m|[A-Z])$/;

// The places a sentence may be clipped: [index where the kept head ends].
export const clauseCuts = (s) => {
  const cuts = [];
  const add = (i) => { if (i > 0 && !cuts.includes(i)) cuts.push(i); };
  let m;
  // a sentence end or a semicolon / colon followed by a space (never "8.4", "1,200", "10:30")
  const end = /([.;:!?])\s+(?=\S)/g;
  while ((m = end.exec(s))) {
    if (m[1] === "." && ABBREV.test(s.slice(0, m.index))) continue;
    if (m[1] === ":" && /\d$/.test(s.slice(0, m.index))) continue;
    add(m.index);
  }
  // a spaced dash ("Diabetic eye exam — last Mar 2024")
  const dash = /\s+[—–]\s+/g;
  while ((m = dash.exec(s))) add(m.index);
  // before a justification clause
  const why = /,?\s+(?=(?:given|due to|because|as evidenced|in light of|in the setting of|which|while|especially|particularly|including|to (?:reduce|prevent|assess|evaluate|monitor|address|ensure))\b)/gi;
  while ((m = why.exec(s))) add(m.index);
  return cuts.sort((a, b) => a - b);
};

// The sentence as a card title: {text, clipped}. Strips one trailing period.
export const clipClause = (text, max = TITLE_MAX) => {
  const s = squash(text);
  const tidy = (t) => t.replace(/[\s,;:—–-]+$/, "").replace(/\.$/, "");
  if (s.length <= max) return { text: tidy(s), clipped: false };
  const cuts = clauseCuts(s).filter((i) => i >= TITLE_MIN);
  const within = cuts.filter((i) => i <= max);
  let at = within.length ? within[within.length - 1] : null;
  if (at == null) {
    const first = cuts.find((i) => i <= TITLE_HARD);
    at = first == null ? null : first;
  }
  if (at == null) return { text: tidy(s), clipped: false };
  const head = tidy(s.slice(0, at));
  if (!head || head.length >= s.length - 1) return { text: tidy(s), clipped: false };
  return { text: `${head}…`, clipped: true };
};

// {text, clipped, source}: the headline when served, else the clipped sentence.
export const recTitle = (rec) => {
  const h = squash(rec && rec.headline);
  if (h) return { text: h, clipped: false, source: "headline" };
  const c = clipClause(rec && rec.text);
  return { text: c.text || "—", clipped: c.clipped, source: c.clipped ? "clip" : "text" };
};

// The full sentence behind the (i), when the title does not already say it.
export const infoDetail = (rec) => {
  const full = squash((rec && (rec.detail || rec.text)) || "");
  if (!full) return "";
  const t = recTitle(rec);
  if (t.source === "text") return "";
  return full;
};

// The (i)'s grey provenance line: "Care gap + recommendation · open since Sep 11 · 2 visits"
// (a bare source acronym only when no guideline link carries it; a reopen note last).
export const infoProvenance = (rec, { whenIsBare = false, hasGuideline = false } = {}) => {
  const seen = [];
  ((rec && rec.provenance && rec.provenance.sources) || []).forEach((s) => {
    const label = SOURCE_LABEL[s.list] || s.list;
    if (label && !seen.includes(label)) seen.push(label);
  });
  const parts = [cap(seen.join(" + "))];
  const when = squash(rec && rec.provenance && rec.provenance.when);
  if (when && !(whenIsBare && hasGuideline)) parts.push(when);
  if (rec && rec.reopened) parts.push(rec.reopen_reason === "order_removed" ? "reopened: order removed" : "reopened: new evidence");
  return parts.filter(Boolean).join(" · ");
};

/* ───────── finding | action (owner 10-07) ───────── */

// "I don't want arrow again since we are often using arrows to indicate labs increasing/decreasing":
// CareIQ writes a served headline as "finding → action"; the row shows the two halves apart (the action
// its own element and colour, RecsDrawer.jsx RecTitle) and never that arrow. Only a SERVED headline
// (careiq.n3.3) is split; a fallback sentence (n3.1 / n3.2 / legacy, or no headline) is shown whole.
// The separators: → ⇒ ⟶ ➜ ➡ ⟹ ⇨ -> => (review 10-07, lead decision B1):
//   value change  never a split point: the text on BOTH sides of the arrow, spaces, units (%, mg/dL,
//                 mmol/L, lb, kg) and parentheses aside, is a number ("61 → 52", "7.2%→8.1%",
//                 "130 mg/dL→98", "(61 → 52)", "148/92 → 138/88") or a month / date ("(Mar→Aug)",
//                 "Mar 2024 → Aug 2025"); kept, and drawn (titleParts "arrow")
//   split point   an arrow outside parentheses whose right side starts with a word (or "↑ / ↓ word":
//                 "→ ↑ CV risk"); the LAST one splits
//                 ("eGFR 61 → 52 ↓ + HTN → CKD w/u": "eGFR 61 → 52 ↓ + HTN" | "CKD w/u"), an earlier
//                 one inside the finding reads as " — "
//   other         (inside parentheses, before a symbol) stays as written, drawn
const ARROW = "(?:→|⇒|⟶|➜|➡\\uFE0F?|⟹|⇨|-+>|=+>)";
const SEP = new RegExp(`\\s*${ARROW}\\s*`, "g");
// the arrow glyphs a title draws (titleParts); "->" / "=>" are plain text in the web font and stay text
const GLYPH = "(?:→|⇒|⟶|➜|➡\\uFE0F?|⟹|⇨)";
const TRAIL_SEP = new RegExp(`\\s*${ARROW}\\s*$`);
const VUNIT = "(?:%|mg\\/dL|mmol\\/L|lbs?|kg)";
const VNUM = "[<>]?[-−]?\\d[\\d,]*(?:\\.\\d+)?(?:\\/\\d+(?:\\.\\d+)?)?";
const VMON = "(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|June?|July?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\\.?";
const LEFT_NUM = new RegExp(`(?:^|[^A-Za-z0-9.])${VNUM}\\s*(?:${VUNIT})?[\\s()]*$`, "i");
const RIGHT_NUM = new RegExp(`^[\\s()]*${VNUM}\\s*(?:${VUNIT})?(?![A-Za-z0-9])`, "i");
const LEFT_DATE = new RegExp(`(?:^|[^A-Za-z])${VMON}(?:\\s+\\d{1,2})?(?:,?\\s+\\d{4})?[\\s()]*$`);
const RIGHT_DATE = new RegExp(`^[\\s()]*${VMON}(?![A-Za-z])`);
export const isValueChange = (left, right) => (LEFT_NUM.test(left) && RIGHT_NUM.test(right)) || (LEFT_DATE.test(left) && RIGHT_DATE.test(right));
const depthAt = (s, i) => {
  let d = 0;
  for (let k = 0; k < i; k += 1) {
    if (s[k] === "(") d += 1;
    else if (s[k] === ")" && d > 0) d -= 1;
  }
  return d;
};
// every separator-shaped arrow in s: {i, j, trend, split}
export const arrowsIn = (text) => {
  const s = str(text);
  const out = [];
  let m;
  SEP.lastIndex = 0;
  while ((m = SEP.exec(s))) {
    const i = m.index;
    const j = i + m[0].length;
    const left = s.slice(0, i);
    const right = s.slice(j);
    const trend = isValueChange(left, right);
    out.push({ i, j, trend, split: !trend && depthAt(s, i) === 0 && /^(?:[↑↓]{1,2}\s*)?[A-Za-z]/.test(right) });
  }
  return out;
};
const tidyHalf = (t) => t.replace(/^[\s,;:·—–-]+/, "").replace(/[\s,;:·—–-]+$/, "");
const joinDash = (a, b) => (a && b ? `${a} — ${b}` : a || b);

// "recheck" -> "Recheck", "f/u endo" -> "F/u endo"; a word that is already cased stays ("eGFR", "SGLT2i").
export const capAction = (s) => (/^[a-z][a-z'-]*(?=$|[\s,.;:?!/])/.test(s) ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// A risk / statement ending is not something to do ("BP avg 148/92 + DM2 → CV risk ↑" says what the
// findings add up to, it asks for nothing): it stays in the finding, in plain text (joined by " — "),
// and the Actions column stays empty. A statement: "risk" alone or with a level ("risk high"), any
// "risk ↑ / ↓", a "↑ CV risk", or an ending in "risk" ("elevated CV risk", "serotonin risk"), unless it
// starts with a verb that asks for something ("reduce CV risk", "assess fall risk", "risk-stratify w/
// ASCVD calc"); one that ends with "?" ("SGLT2i?") is an action (a question to the clinician).
const ACT_VERB = /^(?:risk-stratify|stratify|reduce|lower|decrease|assess|reassess|address|discuss|evaluate|review|counsel|mitigate|minimi[sz]e|manage|monitor|screen|check|recheck|calc(?:ulate)?|estimate|consider|start|stop|hold|treat|limit|modify|optimi[sz]e|document|educate|repeat|order|refer|titrate|intensify|add|switch|avoid)\b/i;
export const isStatement = (a) => {
  const s = squash(a);
  if (!s || /\?$/.test(s)) return false;
  if (/^risk(?:\s*(?:[↑↓]{1,2}|high|low|elevated|increased|moderate|very high))?$/i.test(s)) return true;
  if (ACT_VERB.test(s)) return false;
  if (/^[↑↓]/.test(s) && /\brisk\b/i.test(s)) return true;
  return /\brisk\s*[↑↓]/i.test(s) || /\brisk(?:\s*[↑↓]{1,2})?$/i.test(s);
};

// the first `ch` outside parentheses, or -1
const topLevel = (s, ch) => {
  let d = 0;
  for (let k = 0; k < s.length; k += 1) {
    if (s[k] === "(") d += 1;
    else if (s[k] === ")" && d > 0) d -= 1;
    else if (s[k] === ch && d === 0) return k;
  }
  return -1;
};

// {finding, action}: a served headline split at its split point (above); none -> all finding, no
// action. A statement ending (isStatement) goes back into the finding; "<statement>; <action>"
// ("elevated CV risk; titrate ARB") splits at the ";": the statement to the finding, the rest to do.
export const splitHeadline = (text) => {
  const s = squash(text).replace(TRAIL_SEP, "");   // a dangling arrow at the end says nothing
  const cuts = arrowsIn(s).filter((a) => a.split);
  if (!cuts.length) return { finding: s, action: "" };
  const at = cuts[cuts.length - 1];
  let finding = s.slice(0, at.i);
  cuts.slice(0, -1).reverse().forEach((a) => { finding = `${finding.slice(0, a.i)} — ${finding.slice(a.j)}`; });
  finding = tidyHalf(finding);
  let rest = tidyHalf(s.slice(at.j));
  if (isStatement(rest)) return { finding: joinDash(finding, rest), action: "" };
  const semi = topLevel(rest, ";");
  if (semi > 0) {
    const head = tidyHalf(rest.slice(0, semi));
    const tail = tidyHalf(rest.slice(semi + 1));
    if (tail && isStatement(head)) {
      finding = joinDash(finding, head);
      rest = tail;
    }
  }
  const action = capAction(rest);
  return { finding, action: action || "" };
};

// The headline as one plain line, its halves joined by " · " (no separator arrow): the Current Orders
// reason line, the (i)'s accessible name.
export const plainHeadline = (text) => {
  const h = splitHeadline(text);
  return [h.finding, h.action].filter(Boolean).join(" · ");
};

// The Current Orders reason of an item's order: its `why` (CONTRACT §19: the headline when there is
// one), the headline as one plain line; any other reason as written.
export const reasonText = (why, headline) => {
  const w = squash(why);
  const h = squash(headline);
  return w && h && w === h ? plainHeadline(w) : str(why);
};

// The row's halves after `trim` (recLanes.js trimLead: a served headline loses what the value lead
// already says). The trim runs on the whole "finding → action", so a finding that was nothing but
// the lead leaves the action alone ("UACR 48 ↑ (Aug 2025) → recheck" under the UACR lead: "Recheck").
export const headlineView = (text, trim = (t) => t) => {
  const h = splitHeadline(text);
  if (!h.action) {
    const f = trim(h.finding);
    return { finding: f, action: "", trimmed: f !== h.finding };
  }
  if (!h.finding) return { ...h, trimmed: false };
  const whole = `${h.finding} → ${h.action}`;
  const t = trim(whole);
  if (t === whole) return { ...h, trimmed: false };
  const again = splitHeadline(t);
  return again.action ? { ...again, trimmed: true } : { finding: "", action: capAction(again.finding), trimmed: true };
};

// "Stale since 2024-04-26" -> "Stale since Apr 26, 2024": a served headline's ISO date as the rest of the
// drawer writes dates (owner 10-07, V4 graft "shorter findings"; display only, the date is the item's own).
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const prettyIsoDates = (text) => str(text).replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (m, y, mo, d) => {
  const n = Number(mo);
  const day = Number(d);
  return n >= 1 && n <= 12 && day >= 1 && day <= 31 ? `${MON[n - 1]} ${day}, ${y}` : m;
});

// What one row says: a served headline (careiq.n3.3) as finding | action after the lead trim; anything
// else (a fallback sentence: no headline, n3.1 / n3.2 / legacy) whole, as the finding, no action. `plain`:
// the row as one line without the separator (the (i)'s accessible name).
export const rowText = (rec, trim) => {
  const title = recTitle(rec);
  if (title.source !== "headline") return { title, finding: title.text, action: "", trimmed: false, plain: title.text };
  const v = headlineView(title.text, trim);
  return { title, ...v, finding: prettyIsoDates(v.finding), plain: plainHeadline(title.text) };
};

/* ───────── the title's look ───────── */

const MONTH = "(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*\\.?";
// a parenthesised date: "(Aug 12)", "(last 2014)", "(Mar 2025)", "(since Sep 11)", "(CT Aug 22)"
const DATE_PAREN = new RegExp(`\\((?:[A-Za-z/]+ ){0,2}(?:${MONTH}(?: \\d{1,2})?(?:,? \\d{4})?|\\d{4}|\\d{1,2}/\\d{1,2}(?:/\\d{2,4})?)\\)`, "g");

// Title segments: [{k: "t" | "date" | "arrow", t}]; parenthesised dates are set lighter. The row splits
// a served headline at its split point first (headlineView), so an "arrow" part left here is a value
// change ("61 → 52", "(Mar→Aug)"), drawn (RecsDrawer.jsx TrendArrow: the web font has no → glyph);
// an ASCII "->" stays text.
export const titleParts = (text) => {
  const s = str(text);
  const out = [];
  const push = (k, t) => { if (t) out.push({ k, t }); };
  let last = 0;
  const marks = [];
  let m;
  DATE_PAREN.lastIndex = 0;
  while ((m = DATE_PAREN.exec(s))) marks.push({ i: m.index, j: m.index + m[0].length, k: "date" });
  const glyph = new RegExp(GLYPH, "g");
  while ((m = glyph.exec(s))) {
    const i = m.index;
    if (!marks.some((x) => i >= x.i && i < x.j)) marks.push({ i, j: i + m[0].length, k: "arrow" });
  }
  marks.sort((a, b) => a.i - b.i).forEach((x) => {
    push("t", s.slice(last, x.i));
    push(x.k, s.slice(x.i, x.j));
    last = x.j;
  });
  push("t", s.slice(last));
  return out;
};

// [head parts, tail part]: the last word (or arrow / date) is kept on one line
// with the (i) that follows it, so the (i) never wraps onto a line of its own.
export const splitLastWord = (parts) => {
  const ps = parts.slice();
  const last = ps.pop();
  if (!last) return [[], null];
  if (last.k !== "t") return [ps, last];
  // a trailing lone symbol ("CV risk ↑") stays with the word before it
  const m = /^([\s\S]*?\s)(\S+\s+[^A-Za-z0-9\s]{1,2}|\S+)\s*$/.exec(last.t);
  if (!m) return [ps, { ...last, t: last.t.trim() }];
  return [[...ps, { ...last, t: m[1] }], { ...last, t: m[2] }];
};

// "Add atorvastatin 20 mg" -> "Atorvastatin 20 mg" (the + icon says "add").
export const actionShort = (label) => {
  const s = squash(label).replace(/^add\s+/i, "");
  return cap(s) || squash(label);
};
